import React, { useState } from 'react';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeckIcon from '@mui/icons-material/Deck';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ElevatorIcon from '@mui/icons-material/Elevator';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import KitchenIcon from '@mui/icons-material/Kitchen';
import HeatingIcon from '@mui/icons-material/LocalFireDepartment';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import PetsIcon from '@mui/icons-material/Pets';
import PoolIcon from '@mui/icons-material/Pool';
import TvIcon from '@mui/icons-material/Tv';
import WifiIcon from '@mui/icons-material/Wifi';
import { Box, Paper, Typography } from '@mui/material';

export const stepTitle = 'What amenities does your place have?';
export const stepDescription = 'Select all the amenities that apply.';

const amenities = [
  { id: 'wifi', label: 'Wi-Fi', icon: WifiIcon },
  { id: 'kitchen', label: 'Kitchen', icon: KitchenIcon },
  { id: 'washer', label: 'Washer', icon: LocalLaundryServiceIcon },
  { id: 'ac', label: 'Air Conditioning', icon: AcUnitIcon },
  { id: 'parking', label: 'Free Parking', icon: DirectionsCarIcon },
  { id: 'tv', label: 'TV', icon: TvIcon },
  { id: 'pool', label: 'Pool', icon: PoolIcon },
  { id: 'gym', label: 'Gym', icon: FitnessCenterIcon },
  { id: 'heating', label: 'Heating', icon: HeatingIcon },
  { id: 'balcony', label: 'Balcony', icon: DeckIcon },
  { id: 'pets', label: 'Pet Friendly', icon: PetsIcon },
  { id: 'elevator', label: 'Elevator', icon: ElevatorIcon },
];

const SelectAmenities: React.FC = () => {
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);

  const handleAmenityToggle = (amenityId: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenityId)
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: { xs: '100%', md: 1100 }, // Wider on desktop to accommodate more columns
        width: '100%',
        margin: '0 auto',
        padding: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)', // 2 columns on mobile
            sm: 'repeat(3, 1fr)', // 3 columns on small tablets
            md: 'repeat(4, 1fr)', // 4 columns on medium screens
            lg: 'repeat(6, 1fr)', // 6 columns on large desktop screens
          },
          gap: { xs: 2, md: 3 },
          width: '100%',
          justifyItems: 'center',
        }}
      >
        {amenities.map(amenity => {
          const Icon = amenity.icon;
          const isSelected = selectedAmenities.includes(amenity.id);

          return (
            <Paper
              key={amenity.id}
              elevation={0}
              onClick={() => handleAmenityToggle(amenity.id)}
              sx={{
                width: { xs: '100%', md: 160 }, // Responsive width, fixed on desktop
                height: 120, // Fixed height for all cards
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: isSelected ? '2px solid #1976d2' : '2px solid #e0e0e0',
                backgroundColor: isSelected ? '#f3f8ff' : '#fff',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: isSelected ? '#1976d2' : '#bdbdbd',
                  backgroundColor: isSelected ? '#f3f8ff' : '#f9f9f9',
                },
                padding: 2,
                boxSizing: 'border-box',
              }}
            >
              <Icon
                sx={{
                  fontSize: 40,
                  color: isSelected ? '#1976d2' : '#666',
                  marginBottom: 1,
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: isSelected ? '#1976d2' : '#333',
                  fontWeight: isSelected ? 600 : 400,
                  textAlign: 'center',
                  fontSize: '14px',
                  lineHeight: 1.2,
                }}
              >
                {amenity.label}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default SelectAmenities;
